import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { buildRoute, ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import { useCart } from '../context/CartContext';
import { TestsShimmer } from '../components/shimmer';
import {
  useGetTestsListQuery,
  useSearchTestsQuery,
  useGetDepartmentsQuery,
  usePrefetch,
} from '../store/edosApi';

const PER_PAGE = 12;

// Tests — built to the v5 reference.
//
// The reference filters a hard-coded list of sixteen tests in the browser.
// Ours is the live eDOS catalogue: ~2,078 rows, paginated server-side. So
// every chip here maps to a query parameter the catalogue endpoint already
// understands, which keeps the result count and the pagination honest
// rather than filtering only whatever page happens to be loaded.
//
// Two differences from the reference, both forced by the data:
//   · Its "Age" and "Lifestyle" tabs are not here. The catalogue has no
//     age or lifestyle field, so those chips could only ever return a
//     guess. "Department" takes their place — a real field, and the one
//     filter the reference's own test cards are tagged by.
//   · It shows no price on a card and neither does this. The price still
//     travels into the cart.
//
// `keyword=` is NOT used anywhere below: that parameter 500s on the
// catalogue endpoint. Free-text goes through /get-edos/search instead,
// which is what the search box has always used.

// Friendly label → a substring of the catalogue's `disease` column. The
// column is free text holding 571 distinct values ("Diabetes Mellitus",
// "Cardiovascular Disease (CVD)"), and the endpoint substring-matches it,
// so one approachable word stands in for a whole family. Every keyword
// below was checked against the live catalogue and returns rows.
const CONDITIONS = [
  { label: 'Diabetes', disease: 'Diabetes' },
  { label: 'Heart & cholesterol', disease: 'Cardiovascular' },
  { label: 'Thyroid', disease: 'Thyroid' },
  { label: 'Tiredness & weakness', disease: 'Anaemia' },
  { label: 'Anaemia & blood', disease: 'Anaemia' },
  { label: 'Vitamins', disease: 'Vitamin' },
  { label: 'Liver', disease: 'Liver' },
  { label: 'Kidney & urine', disease: 'Kidney' },
  { label: 'Fever & infection', disease: 'Infection' },
  { label: "Women's health", disease: 'Fertility' },
  { label: 'Cancer screening', disease: 'Cancer' },
  { label: 'Genetic tests', disease: 'Genetic' },
];

const BODY_PARTS = [
  { label: 'Heart', disease: 'Heart' },
  { label: 'Liver', disease: 'Liver' },
  { label: 'Kidney', disease: 'Kidney' },
  { label: 'Thyroid', disease: 'Thyroid' },
  { label: 'Blood', disease: 'Blood' },
  { label: 'Bones & joints', disease: 'Bone' },
  { label: 'Nerves & brain', disease: 'Neuro' },
  { label: 'Hormones', disease: 'Hormonal' },
  { label: 'Immunity', disease: 'Autoimmune' },
  { label: 'Allergy', disease: 'Allergy' },
];

const GENDERS = [
  { label: 'Women', gender: 'Female' },
  { label: 'Men', gender: 'Male' },
];

const TABS = ['Condition', 'Body part', 'Department', 'Gender'];

// "None", blank and "-" all mean the same thing in the catalogue.
const prepLine = (row) => {
  const f = String(row.fasting || '').trim();
  if (!f || /^(none|no|n\/a|-)$/i.test(f)) return 'No fasting needed';
  return f;
};

const mapRow = (r) => ({
  id: r.id,
  name: r.test_name,
  area: (r.departments || r.department || '').split(',')[0].trim() || 'Pathology',
  tat: r.report_tat || r.tat || '',
  sample: r.sample_type || '—',
  prep: prepLine(r),
  // Booking's slot step highlights early-morning slots when anything in
  // the visit needs a fast, so the flag has to travel with the cart item.
  fasting: !/^(none|no|n\/a|-|)$/i.test(String(r.fasting || '').trim()),
  price: Number(r.mrp) || 0,
});

const Tests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleItem, inCart, totals } = useCart();

  const urlQ = searchParams.get('q') || '';
  const urlC = searchParams.get('c') || '';

  const [q, setQ] = useState(urlQ);
  const [submittedQ, setSubmittedQ] = useState(urlQ);
  const [tab, setTab] = useState(urlC ? 'Condition' : 'Condition');
  // The chip label, or 'All'. Held as a label so the active pill and the
  // results heading agree without a second lookup.
  const [pick, setPick] = useState(() => (
    CONDITIONS.some((c) => c.label === urlC) ? urlC : 'All'
  ));
  const [page, setPage] = useState(1);

  // Re-sync when the URL changes under us — the header's search panel
  // links straight here with ?q= or ?c=.
  useEffect(() => {
    const nextQ = searchParams.get('q') || '';
    const nextC = searchParams.get('c') || '';
    if (nextQ !== submittedQ) {
      setQ(nextQ);
      setSubmittedQ(nextQ);
    }
    if (nextC && CONDITIONS.some((c) => c.label === nextC)) {
      setTab('Condition');
      setPick(nextC);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: apiDepartments = [] } = useGetDepartmentsQuery();

  // The chips for the current tab, each carrying the query parameter it
  // sets. Department comes from the catalogue itself.
  const chips = useMemo(() => {
    if (tab === 'Condition') return CONDITIONS;
    if (tab === 'Body part') return BODY_PARTS;
    if (tab === 'Gender') return GENDERS;
    return apiDepartments.map((d) => ({ label: d, department: d }));
  }, [tab, apiDepartments]);

  const active = chips.find((c) => c.label === pick) || null;
  const isSearching = submittedQ.trim().length > 0;

  const listArgs = {
    page,
    perPage: PER_PAGE,
    ...(active?.disease ? { disease: active.disease } : null),
    ...(active?.department ? { department: active.department } : null),
    ...(active?.gender ? { gender: active.gender } : null),
  };

  const listQuery = useGetTestsListQuery(listArgs, { skip: isSearching });
  const searchQuery = useSearchTestsQuery(
    { typewords: submittedQ.trim(), page, perPage: PER_PAGE },
    { skip: !isSearching },
  );

  const source = isSearching ? searchQuery : listQuery;
  const { data, isLoading, isFetching, isError } = source;

  const pagination = data?.pagination ?? null;
  const rows = useMemo(() => (data?.items ?? []).map(mapRow), [data]);

  // Prefetch the next page so paging feels instant.
  const prefetchList = usePrefetch('getTestsList');
  const prefetchSearch = usePrefetch('searchTests');
  useEffect(() => {
    if (!pagination || pagination.current_page >= pagination.last_page) return;
    if (isSearching) prefetchSearch({ typewords: submittedQ.trim(), page: page + 1, perPage: PER_PAGE });
    else prefetchList({ ...listArgs, page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, page, isSearching, submittedQ, tab, pick]);

  // Any change of filter starts again at page one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [submittedQ, tab, pick]);

  // The reference filters as you type. Ours costs a request per change,
  // so the field settles for 400ms first — long enough that typing a test
  // name is one call, short enough to still feel live.
  useEffect(() => {
    if (q === submittedQ) return undefined;
    const t = setTimeout(() => {
      setSubmittedQ(q);
      setSearchParams(q.trim() ? { q: q.trim() } : {}, { replace: true });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Enter submits immediately rather than waiting out the debounce.
  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    setSubmittedQ(q);
    setSearchParams(q.trim() ? { q: q.trim() } : {}, { replace: true });
  };

  const onPickTab = (t) => {
    setTab(t);
    setPick('All');
  };

  const onPickChip = (label) => {
    setPick((cur) => (cur === label ? 'All' : label));
  };

  const clearAll = () => {
    setPick('All');
    setQ('');
    setSubmittedQ('');
    setSearchParams({});
  };

  const hasFilter = pick !== 'All' || isSearching;
  const total = pagination?.total ?? rows.length;

  const resultKicker = isSearching ? 'Search' : (pick === 'All' ? 'All tests' : tab);
  const resultTitle = isSearching
    ? `Results for “${submittedQ.trim()}”`
    : (pick === 'All' ? 'Popular tests' : pick);
  const countLabel = isLoading
    ? 'Loading…'
    : `${total.toLocaleString('en-IN')} test${total === 1 ? '' : 's'}`;

  // ── shared fragments ────────────────────────────────────────────────
  const panel = { width: '100%', maxWidth: 1320, mx: 'auto' };

  const h2Dark = {
    m: 0,
    // Explicit: MUI's Typography carries body1's dark colour, which beats
    // the panel's inherited white and left this near-invisible.
    color: '#FFFFFF',
    fontFamily: DISPLAY,
    fontWeight: 700,
    fontSize: 'clamp(28px, 3.4vw, 40px)',
    lineHeight: 1,
    letterSpacing: '-.03em',
  };

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            ...panel,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-teal)',
            color: '#FFFFFF',
          }}
        >
          <Box
            component="span"
            sx={{
              alignSelf: 'start',
              px: '12px',
              py: '7px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-yellow)',
              color: 'var(--tl-g9)',
              fontFamily: UI,
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {`${(pagination?.total ?? 2000).toLocaleString('en-IN')}+ tests · NABL ISO 15189:2022`}
          </Box>

          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(40px, 6vw, 72px)',
              lineHeight: 0.95,
              letterSpacing: '-.035em',
              color: '#FFFFFF',
            }}
          >
            Find your test.
          </Typography>

          <Typography
            sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: 'var(--tl-teal-50)', maxWidth: 560 }}
          >
            Add as many as you need. We collect them all in one home visit.
          </Typography>

          <Box
            component={Link}
            to={ROUTES.DOCTOR_CONSULT}
            sx={{
              alignSelf: 'start',
              color: '#FFFFFF',
              fontFamily: UI,
              fontSize: 15,
              fontWeight: 800,
              textDecoration: 'underline',
              '&:hover': { color: '#FFFFFF' },
            }}
          >
            Not sure which test? Talk to a lab doctor →
          </Box>

          <Box
            component="form"
            onSubmit={onSubmitSearch}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 0.75,
              borderRadius: '20px',
              bgcolor: '#FFFFFF',
              maxWidth: 760,
            }}
          >
            {/* Visually hidden, not display:none — the input still needs a
                label for screen readers. The sizes are strings on purpose:
                in MUI's sx a bare `width: 1` means 100%, not one pixel,
                which stretched this across the page. */}
            <Box
              component="label"
              htmlFor="tests-q"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                p: 0,
                m: '-1px',
                overflow: 'hidden',
                clipPath: 'inset(50%)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Search by test name
            </Box>
            <Box
              component="input"
              id="tests-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by test name"
              sx={{
                flex: 1,
                minWidth: 200,
                border: 0,
                outline: 0,
                bgcolor: 'transparent',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 17,
                p: '14px 16px',
              }}
            />
            {/* The reference puts "Upload prescription" here, not a
                search button — the field filters as you type. Enter still
                submits, for anyone who reaches for it. */}
            <Box
              component={Link}
              to={ROUTES.BOOKING}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '14px',
                p: '14px 18px',
                bgcolor: 'var(--tl-g9)',
                color: '#FFFFFF',
                fontFamily: UI,
                fontSize: 15,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-g8)', color: '#FFFFFF' },
              }}
            >
              Upload prescription
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── 2. browse by ─────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 1.5, px: 2 }}>
        <Box
          sx={{
            ...panel,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            p: 'clamp(22px, 4vw, 40px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'end',
              justifyContent: 'space-between',
              gap: '18px 28px',
            }}
          >
            <Typography component="h2" sx={h2Dark}>Browse by</Typography>

            {/* The rail scrolls sideways rather than wrapping, so the
                tabs stay on one line on a phone. */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                p: 0.625,
                maxWidth: '100%',
                borderRadius: '999px',
                bgcolor: 'var(--tl-g7)',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {TABS.map((t) => (
                <Box
                  key={t}
                  component="button"
                  type="button"
                  onClick={() => onPickTab(t)}
                  aria-pressed={t === tab}
                  sx={{
                    border: 0,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    p: '11px 18px',
                    whiteSpace: 'nowrap',
                    // Without this the four tabs shrink into each other
                    // rather than letting the rail scroll sideways.
                    flexShrink: 0,
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 800,
                    transition: `background-color .25s ${EASE}, color .25s ${EASE}`,
                    bgcolor: t === tab ? 'var(--tl-yellow)' : 'transparent',
                    color: t === tab ? 'var(--tl-g9)' : 'var(--tl-line-2)',
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[{ label: 'All' }, ...chips].map((c) => {
              const on = c.label === pick;
              return (
                <Box
                  key={c.label}
                  component="button"
                  type="button"
                  onClick={() => (c.label === 'All' ? setPick('All') : onPickChip(c.label))}
                  aria-pressed={on}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '999px',
                    p: '11px 18px',
                    whiteSpace: 'nowrap',
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 700,
                    transition: `background-color .25s ${EASE}, border-color .25s ${EASE}`,
                    border: `1.5px solid ${on ? 'var(--tl-teal-l)' : '#3A5F58'}`,
                    bgcolor: on ? 'var(--tl-teal-l)' : 'transparent',
                    color: on ? 'var(--tl-g9)' : '#FFFFFF',
                    '&:hover': { borderColor: on ? 'var(--tl-teal-l)' : 'var(--tl-teal-l)' },
                  }}
                >
                  {c.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── 3. results header ────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          ...panel,
          pt: 4.5,
          px: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'end',
          justifyContent: 'space-between',
          gap: '12px 20px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontFamily: UI,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--tl-teal-d)',
            }}
          >
            {resultKicker}
          </Box>
          <Typography
            component="h2"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.6vw, 44px)',
              lineHeight: 1,
              letterSpacing: '-.03em',
              color: 'var(--tl-g9)',
              overflowWrap: 'anywhere',
            }}
          >
            {resultTitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 700, color: 'var(--tl-g9)' }}>
            {countLabel}
          </Box>
          {hasFilter && (
            <Box
              component="button"
              type="button"
              onClick={clearAll}
              sx={{
                cursor: 'pointer',
                border: '1.5px solid var(--tl-g9)',
                bgcolor: 'transparent',
                color: 'var(--tl-g9)',
                borderRadius: '999px',
                p: '9px 16px',
                fontFamily: UI,
                fontSize: 14,
                fontWeight: 800,
                '&:hover': { bgcolor: 'var(--tl-g9)', color: '#FFFFFF' },
              }}
            >
              Clear ×
            </Box>
          )}
        </Box>
      </Box>

      {/* ── 4. the grid ──────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ ...panel, pt: 1.5, px: 2, pb: totals.count > 0 ? '140px' : '72px' }}
      >
        {isLoading ? (
          <TestsShimmer count={PER_PAGE} />
        ) : isError ? (
          <Box
            sx={{
              p: 4,
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1.5px solid var(--tl-line)',
              textAlign: 'center',
              fontFamily: UI,
              color: 'var(--tl-muted)',
            }}
          >
            We couldn&rsquo;t load the catalogue just now. Please try again.
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              p: 4,
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1.5px solid var(--tl-line)',
              textAlign: 'center',
              fontFamily: UI,
              color: 'var(--tl-muted)',
            }}
          >
            No tests match that. Try another filter, or{' '}
            <Box component="button" type="button" onClick={clearAll} sx={{ border: 0, bgcolor: 'transparent', p: 0, font: 'inherit', fontWeight: 800, color: 'var(--tl-teal-d)', cursor: 'pointer' }}>
              clear the filters
            </Box>.
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))',
                gap: 1.5,
                opacity: isFetching ? 0.55 : 1,
                transition: 'opacity .15s',
              }}
            >
              {rows.map((t) => {
                const added = inCart(t.id, 'test');
                return (
                  <Box
                    key={t.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.75,
                      minHeight: 240,
                      p: '22px',
                      borderRadius: '24px',
                      bgcolor: '#FFFFFF',
                      border: '1.5px solid var(--tl-line)',
                      boxShadow: '0 10px 24px -18px rgba(11,43,39,0.35)',
                      transition: `border-color .25s ${EASE}`,
                      '&:hover': { borderColor: 'var(--tl-teal)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          px: '10px',
                          py: '6px',
                          borderRadius: '999px',
                          bgcolor: 'var(--tl-teal)',
                          color: '#FFFFFF',
                          fontFamily: UI,
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        {t.area}
                      </Box>
                      {t.tat && (
                        <Box
                          component="span"
                          sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)', textAlign: 'right' }}
                        >
                          {t.tat}
                        </Box>
                      )}
                    </Box>

                    <Box
                      component={Link}
                      to={buildRoute(ROUTES.TEST_DETAIL, { id: t.id })}
                      sx={{
                        flex: 1,
                        fontFamily: DISPLAY,
                        fontWeight: 700,
                        fontSize: 22,
                        lineHeight: 1.15,
                        letterSpacing: '-.01em',
                        color: 'var(--tl-g9)',
                        textDecoration: 'none',
                        '&:hover': { color: 'var(--tl-teal-d)' },
                      }}
                    >
                      {t.name}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        fontFamily: UI,
                        fontSize: 14,
                        color: 'var(--tl-muted-2)',
                      }}
                    >
                      <Box component="span">Sample: {t.sample}</Box>
                      <Box component="span">{t.prep}</Box>
                    </Box>

                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleItem({ id: t.id, type: 'test', name: t.name, price: t.price, tat: t.tat, fasting: t.fasting })}
                      sx={{
                        border: 0,
                        cursor: 'pointer',
                        borderRadius: '14px',
                        p: '13px',
                        fontFamily: UI,
                        fontSize: 15,
                        fontWeight: 800,
                        transition: `background-color .25s ${EASE}`,
                        bgcolor: added ? 'var(--tl-teal)' : 'var(--tl-mint)',
                        color: added ? '#FFFFFF' : 'var(--tl-g9)',
                      }}
                    >
                      {added ? 'Added ✓' : 'Add to visit'}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* The reference has sixteen tests and so needs no paging.
                With 2,078 the strip stays. */}
            {pagination && pagination.last_page > 1 && (
              <Box
                component="nav"
                aria-label="Pagination"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.75, mt: 6 }}
              >
                {[
                  { label: '← Prev', to: Math.max(1, page - 1), off: pagination.current_page <= 1 },
                  { label: 'Next →', to: Math.min(pagination.last_page, page + 1), off: pagination.current_page >= pagination.last_page },
                ].map((b, i) => (
                  <Box
                    key={b.label}
                    component="button"
                    type="button"
                    onClick={() => setPage(b.to)}
                    disabled={b.off || isFetching}
                    sx={{
                      order: i === 0 ? 0 : 2,
                      cursor: 'pointer',
                      borderRadius: '999px',
                      p: '12px 20px',
                      fontFamily: UI,
                      fontSize: 15,
                      fontWeight: 800,
                      border: '1.5px solid var(--tl-g9)',
                      bgcolor: i === 0 ? 'transparent' : 'var(--tl-g9)',
                      color: i === 0 ? 'var(--tl-g9)' : '#FFFFFF',
                      '&:disabled': { opacity: 0.4, pointerEvents: 'none' },
                    }}
                  >
                    {b.label}
                  </Box>
                ))}
                <Box
                  component="span"
                  sx={{ order: 1, fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)', fontVariantNumeric: 'tabular-nums' }}
                >
                  Page {pagination.current_page} of {pagination.last_page}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── 5. the sticky visit bar ──────────────────────────────────── */}
      {totals.count > 0 && (
        <Box
          sx={{
            position: 'fixed',
            left: 12,
            // Clears the floating WhatsApp button, which sits at
            // bottom/right 24 and would otherwise cover this bar's CTA on
            // a phone. From sm up the bar is centred and narrow enough
            // that the two never meet.
            right: { xs: 88, sm: 12 },
            bottom: 12,
            zIndex: 500,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
              maxWidth: 560,
              p: '8px 8px 8px 22px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-g9)',
              color: '#FFFFFF',
              boxShadow: '0 18px 40px -14px rgba(0,0,0,0.5)',
            }}
          >
            <Box
              component="span"
              sx={{ fontFamily: UI, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {`${totals.count} test${totals.count === 1 ? '' : 's'}`}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> in your visit</Box>
            </Box>
            <Box
              component={Link}
              to={ROUTES.BOOKING}
              sx={{
                px: '20px',
                py: '12px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 15,
                fontWeight: 800,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'var(--tl-yellow-d)', color: 'var(--tl-g9)' },
              }}
            >
              Book
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>&nbsp;home</Box>
              &nbsp;visit
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Tests;
